import React, { useRef } from 'react';
import classNames from 'classnames';
import './ExpandableSection.scss';

/**
 * Controlled expandable "selector" row used in the new Job Poster funnel (GO4-1728 / GO4-1701).
 *
 * Matches the Figma "selector" component: a title + subtitle header with a +/- toggle on the
 * right and a divider line above. The parent owns the open state so that, per the ticket, the
 * top-level content sections behave as a singular accordion (opening one collapses the others).
 * Nested usages can be toggled independently — that is purely a function of how the parent wires
 * `expanded` / `onToggle`.
 *
 * @param {node}     title     Header title (Montserrat Bold 16).
 * @param {node}     subtitle  Optional supporting line (Gotham Medium 14).
 * @param {boolean}  expanded  Whether this section is currently open.
 * @param {function} onToggle  Toggle handler owned by the parent.
 * @param {boolean}  nested    Renders the tighter, indented variant used inside a section.
 * @param {node}     children  Body content revealed when expanded.
 */
const ExpandableSection = ({ title, subtitle, expanded, onToggle, nested, children }) => {
  const contentRef = useRef(null);

  return (
    <div className={classNames('expandable-section', { expanded, 'expandable-section--nested': nested })}>
      <button
        type="button"
        className="expandable-section__header"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <span className="expandable-section__txt">
          <span className="expandable-section__title">{title}</span>
          {subtitle && <span className="expandable-section__subtitle">{subtitle}</span>}
        </span>
        <i
          className={classNames('expandable-section__toggle fas', {
            'fa-minus': expanded,
            'fa-plus': !expanded,
          })}
          aria-hidden="true"
        />
      </button>
      <div
        className="expandable-section__body"
        style={{ maxHeight: expanded ? `${contentRef.current?.scrollHeight ?? 0}px` : 0 }}
      >
        <div ref={contentRef} className="expandable-section__content">
          {children}
        </div>
      </div>
    </div>
  );
};

export default ExpandableSection;
